import React, { lazy, Suspense } from 'react';
import './App.css';
import './buttons.css';

import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import { createBrowserHistory } from 'history';
import { Route, Router, Switch } from 'react-router-dom';

import Loader from './Util/Loader/Loader';
import PageMiss from './404/404';
import { syncOutletFcmToken } from './Util/NativeApp';
// Imported for its side effect: it must record how the document was loaded (refresh or not) at startup.
import './Util/ReloadLanding';

const Login = lazy(() => import('./Login/Login'));
const Dashboard = lazy(() => import('./Dashboard/Dashboard'));
const AppControls = lazy(() => import('./AppControls/AppControls'));
const Warehouses = lazy(() => import('./Warehouses/Warehouses'));
const Categories = lazy(() => import('./Categories/Categories'));
const SubCategories = lazy(() => import('./Categories/SubCategories'));
const Products = lazy(() => import('./Products/Products'));
const Banners = lazy(() => import('./Banners/Banners'));
const CouponGroup = lazy(() => import('./Coupon/CouponGroup'));
const Coupon = lazy(() => import('./Coupon/Coupon'));
const CouponAnalytics = lazy(() => import('./Coupon/CouponAnalytics'));
const HomepageHeaders = lazy(() => import('./HomepageHeaders/HomepageHeaders'));
const Orders = lazy(() => import('./Orders/Orders'));
const OrderDetail = lazy(() => import('./OrderDetail/OrderDetail'));
const Invoice = lazy(() => import('./Invoice/Invoice'));
const AddonHeader = lazy(() => import('./AddonHeaders/AddonHeader'));
const UsersList = lazy(() => import('./UsersList/UsersList'));
const Stamp = lazy(() => import('./Stamps/Stamp'));
const StampAnalytics = lazy(() => import('./Stamps/StampAnalytics'));
const Subscription = lazy(() => import('./Subscription/Subscription'));
const SubscriptionAnalytics = lazy(() => import('./Subscription/SubscriptionAnalytics'));
const ScanAndPay = lazy(() => import('./ScanAndPay/ScanAndPay'));
const ProductGroup = lazy(() => import('./ProductGroup/ProductGroup'));
const Brands = lazy(() => import('./Brands/Brands'));
const DeleteQuery = lazy(() => import('./DeleteQuery/DeleteQuery'));
const RechargeWallet = lazy(() => import('./RechargeWallet/RechargeWallet'));
const AppGames = lazy(() => import('./AppGames/AppGames'));
const OrderFeedbacks = lazy(() => import('./OrderFeedbacks/OrderFeedbacks'));
const ProductsManagement = lazy(() => import('./ProductsManagement/ProductsManagement'));
const MerchantOrders = lazy(() => import('./MerchantOrders/MerchantOrders'));
const UserOrders = lazy(() => import('./UserOrders/UserOrders'));
const AbandonedStamps = lazy(() => import('./AbandonedStamps/AbandonedStamps'));
const StampRedeem = lazy(() => import('./StampRedeem/StampRedeem'));

const getStoredThemeColors = () => {
  const savedTheme = localStorage.getItem('adminPortalThemeColors');

  if (!savedTheme) return null;

  try {
    return JSON.parse(savedTheme);
  } catch (error) {
    localStorage.removeItem('adminPortalThemeColors');
    return null;
  }
};

const getRootCssColor = (name) => {
  if (typeof window === 'undefined') return '';

  const value = getComputedStyle(document.documentElement).getPropertyValue(name);

  return value ? value.trim() : '';
};

const applyThemeColorsToRoot = (colors) => {
  if (!colors) return;

  if (colors.prime) {
    document.documentElement.style.setProperty('--prime', colors.prime);
  }

  if (colors.secondPrime) {
    document.documentElement.style.setProperty('--secondPrime', colors.secondPrime);
  }

  if (colors.primeSubtle) {
    document.documentElement.style.setProperty('--primeSubtle', colors.primeSubtle);
  }

  if (colors.prime && colors.secondPrime) {
    document.documentElement.style.setProperty('--gradient', `linear-gradient(45deg, ${colors.prime}, ${colors.secondPrime})`);
  }
};

const applySavedAdminThemeColors = () => {
  const colors = getStoredThemeColors();
  applyThemeColorsToRoot(colors);
  return colors;
};

const createAppTheme = () => {
  const colors = getStoredThemeColors();

  const primaryMain = colors?.prime || getRootCssColor('--prime');
  const secondaryMain = colors?.secondPrime || getRootCssColor('--secondPrime');

  const palette = {};

  if (primaryMain) {
    palette.primary = {
      main: primaryMain,
    };
  }

  if (secondaryMain) {
    palette.secondary = {
      main: secondaryMain,
    };
  }

  return createTheme({
    palette,
  });
};

const isAllowedRoute = (path, loginType) => {
  if (!loginType) return path === '/';

  // Outlet-only pages.
  if (path.startsWith('/products-management') || path.startsWith('/merchant-orders')) return loginType === 'outlet';

  if (loginType === 'admin') return true;

  const outletAllowedRoutes = [
    '/categories',
    '/products',
    '/orders',
    '/order-feedbacks',
    '/order-detail',
    '/invoice',
    '/banners',
    '/sections',
    '/addon-headers',
    '/tags',
    '/scan-and-pay',
  ];

  return outletAllowedRoutes.some((route) => path.startsWith(route));
};

const SecureRoute = ({ component: Component, ...rest }) => {
  return (
    <Route
      {...rest}
      render={(props) => {
        const loginType = localStorage.getItem('loginType');
        const currentPath = props.location.pathname;

        if (!localStorage.getItem('token') && !localStorage.getItem('outlet_token')) {
          return props.history.replace('/');
        }

        if (!isAllowedRoute(currentPath, loginType)) {
          return props.history.replace(loginType === 'admin' ? '/dashboard' : '/categories');
        }

        return <Component {...props} />;
      }}
    />
  );
};

class App extends React.Component {
  constructor(props) {
    super(props);

    applySavedAdminThemeColors();

    this.state = {
      theme: createAppTheme(),
    };

    this.customHistory = createBrowserHistory();
  }

  componentDidMount() {
    applySavedAdminThemeColors();

    this.setState({
      theme: createAppTheme(),
    });

    window.addEventListener('adminPortalThemeColorsUpdated', this.handleThemeColorsUpdated);

    // Inside the Android app: covers outlets already logged in and FCM token refreshes.
    window.addEventListener('loyaloop:fcmToken', this.handleFcmToken);
    syncOutletFcmToken();
  }

  componentWillUnmount() {
    window.removeEventListener('adminPortalThemeColorsUpdated', this.handleThemeColorsUpdated);
    window.removeEventListener('loyaloop:fcmToken', this.handleFcmToken);
  }

  handleFcmToken = () => syncOutletFcmToken();

  handleThemeColorsUpdated = () => {
    applySavedAdminThemeColors();

    this.setState({
      theme: createAppTheme(),
    });
  };

  render = () => {
    return (
      <ThemeProvider theme={this.state.theme}>
        <Router history={this.customHistory}>
          <Suspense
            fallback={
              <div className="loader-container">
                <Loader />
              </div>
            }
          >
            <Switch>
              <Route exact path="/" component={Login} />
              <SecureRoute path="/dashboard" component={Dashboard} />
              <SecureRoute path="/app-controls" component={AppControls} />
              <SecureRoute exact path="/outlet/:outletName/:outletId/categories" component={Categories} />
              <SecureRoute path="/banners" component={Banners} />
              <SecureRoute path="/sections" component={HomepageHeaders} />
              <SecureRoute path="/categories" component={Categories} />
              <SecureRoute path="/brands" component={Brands} />
              <SecureRoute path="/subcategories/:parentId" component={SubCategories} />
              <SecureRoute exact path="/outlets" component={Warehouses} />
              <SecureRoute path="/products/outlet/:outletId/" component={Products} />
              <SecureRoute path="/products/" component={Products} />
              <SecureRoute exact path="/coupon-groups" component={CouponGroup} />
              <SecureRoute exact path="/coupons" component={Coupon} />
              <SecureRoute path="/coupon-analytics" component={CouponAnalytics} />
              <SecureRoute path="/orders" component={Orders} />
              <SecureRoute path="/order-detail/:orderId" component={OrderDetail} />
              <SecureRoute path="/invoice/:orderId" component={Invoice} />
              <SecureRoute path="/addon-headers" component={AddonHeader} />
              <SecureRoute path="/users" component={UsersList} />
              <SecureRoute path="/user-orders/:mobile" component={UserOrders} />
              <SecureRoute path="/abandoned-stamps" component={AbandonedStamps} />
              <SecureRoute path="/stamp-redeem" component={StampRedeem} />
              <SecureRoute exact path="/stamps" component={Stamp} />
              <SecureRoute path="/stamps/analytics" component={StampAnalytics} />
              <SecureRoute exact path="/subscriptions" component={Subscription} />
              <SecureRoute path="/subscriptions/analytics" component={SubscriptionAnalytics} />
              <SecureRoute path="/scan-and-pay" component={ScanAndPay} />
              <SecureRoute path="/product-grouping" component={ProductGroup} />
              <SecureRoute path="/delete-query" component={DeleteQuery} />
              <SecureRoute path="/recharge-wallet" component={RechargeWallet} />
              <SecureRoute path="/app-games" component={AppGames} />
              <SecureRoute path="/order-feedbacks" component={OrderFeedbacks} />
              <SecureRoute path="/products-management" component={ProductsManagement} />
              <SecureRoute path="/merchant-orders" component={MerchantOrders} />

              <Route component={PageMiss} />
            </Switch>
          </Suspense>
        </Router>
      </ThemeProvider>
    );
  };
}

export default App;